import { Capacitor } from "@capacitor/core";
import Axios from "axios";
import { REMOVE_NOTIFICATION_TOKEN_URL } from "../configs";

/**
 * Native (Android/iOS) push for the Capacitor shells.
 *
 * The web Firebase SDK / service worker cannot deliver to the OS notification panel inside a
 * native WebView, so the shells register a real FCM/APNs token through
 * @capacitor/push-notifications. The server sends native tokens a `notification` payload, so the
 * OS shows it with the app killed, backgrounded or open-but-not-focused; tapping it comes back here
 * as `pushNotificationActionPerformed` and is routed in-app.
 */

export const isNativePush = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

export const nativePlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch (e) {
    return "web";
  }
};

const PENDING_PATH_KEY = "pendingPushPath";
const NATIVE_TOKEN_KEY = "nativePushToken";

/**
 * Turn a push `click_action` (absolute URL from the server, possibly with the Laravel /public
 * prefix) into an in-app router path, or null if it is not usable.
 */
export const normalizePushPath = (url) => {
  if (!url) return null;
  let path = String(url);
  try {
    if (/^https?:\/\//i.test(path)) {
      const parsed = new URL(path);
      path = parsed.pathname + parsed.search + parsed.hash;
    }
    if (path === "/public" || path.indexOf("/public?") === 0) {
      path = "/" + path.slice("/public".length);
    } else if (path.indexOf("/public/") === 0) {
      path = path.slice("/public".length) || "/";
    }
    if (
      path === "/my-favorite-stores" ||
      path.indexOf("/my-favorite-stores?") === 0 ||
      path === "/my-favorite" ||
      path.indexOf("/my-favorite?") === 0
    ) {
      path = "/my-wishlist?tab=stores";
    }
  } catch (e) {
    return null;
  }
  return path.startsWith("/") ? path : null;
};

/** A tap that arrived while logged out is kept and replayed after login. */
export const setPendingPushPath = (path) => {
  try {
    if (path) sessionStorage.setItem(PENDING_PATH_KEY, path);
  } catch (e) {
    /* storage unavailable */
  }
};

export const takePendingPushPath = () => {
  try {
    const path = sessionStorage.getItem(PENDING_PATH_KEY);
    if (path) sessionStorage.removeItem(PENDING_PATH_KEY);
    return path || null;
  } catch (e) {
    return null;
  }
};

let listenersBound = false;
let latestHandlers = {};

/**
 * @param {object} opts
 * @param {(token: string) => void} opts.onToken       called with the native device token
 * @param {(path: string) => void} opts.onNavigate     user tapped a notification -> in-app path
 * @param {(n: object) => void} [opts.onForeground]    push received while the app is open
 * @param {boolean} [opts.register=true]  false = only bind tap listeners (no permission prompt / token yet),
 *                                        so a tap that cold-starts the app before login is still captured
 * @returns {Promise<boolean>} true when native push is active
 */
export const initNativePush = async ({ onToken, onNavigate, onForeground, register = true }) => {
  if (!isNativePush()) return false;

  // Handlers change between renders/logins; listeners are bound once and read the latest. Merge, never
  // replace: a later call that only binds tap listeners (register: false, no onToken) must not wipe the
  // onToken of a registration that is still in flight, or the device token is dropped and never saved.
  latestHandlers = {
    ...latestHandlers,
    ...(onToken ? { onToken } : {}),
    ...(onNavigate ? { onNavigate } : {}),
    ...(onForeground ? { onForeground } : {}),
  };

  let PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch (e) {
    console.warn("[nativePush] plugin unavailable", e);
    return false;
  }

  if (!listenersBound) {
    listenersBound = true;

    PushNotifications.addListener("registration", (token) => {
      try {
        localStorage.setItem(NATIVE_TOKEN_KEY, token.value);
      } catch (e) {
        /* storage unavailable */
      }
      if (latestHandlers.onToken && token && token.value) latestHandlers.onToken(token.value);
    });
    PushNotifications.addListener("registrationError", (error) => {
      console.warn("[nativePush] registration failed", error);
    });
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      if (latestHandlers.onForeground) latestHandlers.onForeground(notification);
    });
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = (action && action.notification && action.notification.data) || {};
      const path = normalizePushPath(data.click_action);
      if (path && latestHandlers.onNavigate) latestHandlers.onNavigate(path);
    });
  }

  if (!register) return true;

  try {
    if (nativePlatform() === "android") {
      // Channels must exist before the first push; importance 5 = heads-up + sound.
      await PushNotifications.createChannel({
        id: "new_orders",
        name: "New orders",
        description: "New delivery requests",
        importance: 5,
        visibility: 1,
        vibration: true,
      });
      await PushNotifications.createChannel({
        id: "order_updates",
        name: "Order updates",
        description: "Order and account updates",
        importance: 4,
        visibility: 1,
        vibration: true,
      });
    }

    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== "granted") {
      return false;
    }
    await PushNotifications.register();
    return true;
  } catch (e) {
    console.warn("[nativePush] init failed", e);
    return false;
  }
};

/** App/platform metadata sent with the token so the server routes pushes to the right app. */
export const pushTokenMeta = (app) => ({ app, platform: nativePlatform() });

/**
 * Logout / account switch: tell the server to stop pushing to this device for the signed-in user.
 * Fire-and-forget; never blocks or fails the logout.
 */
export const removeNativePushToken = (authToken) => {
  if (!isNativePush() || !authToken) return Promise.resolve();
  let token = null;
  try {
    token = localStorage.getItem(NATIVE_TOKEN_KEY);
    localStorage.removeItem(NATIVE_TOKEN_KEY);
  } catch (e) {
    /* storage unavailable */
  }
  if (!token) return Promise.resolve();
  return Axios.post(
    REMOVE_NOTIFICATION_TOKEN_URL,
    { push_token: token },
    { headers: { Authorization: `Bearer ${authToken}` } }
  ).catch(() => null);
};
